import React, { useEffect, useMemo, useState } from 'react';
import { Card } from 'primereact/card';
import { Toast } from 'primereact/toast';
import { Icon } from '@iconify/react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { useAuth } from '../contexts/AuthContext';
import { useChoice } from '../contexts/ChoicesContext';
import { ComboNav } from '../components/NavigationPath';
import Loader from '../components/Loader';
import Layout2 from '../components/Layout2';
import AddChoice from '../components/AddChoice';
import PageHeader from '../components/PageHeader';

function ChoiceList() {
  const { toast, restaurantId, auth, hasPermission } = useAuth();
  const { getAllChoices, getSingleChoice } = useChoice();

  const canCreate = useMemo(() => hasPermission("products", "create"),  [hasPermission]);
  const canUpdate = useMemo(() => hasPermission("products", "update"), [hasPermission]);
  const canDelete = useMemo(() => hasPermission("products", "delete"), [hasPermission]);  

  const [choicesList, setChoicesList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editChoiceId, setEditChoiceId] = useState(null);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalRenderedRecords, setTotalRenderedRecords] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [choiceDetails, setChoiceDetails] = useState({
    name: '',
    plu: '',
    products: [],
    visible: "",
    image: "",
    restaurant_id: restaurantId,
    multi_select: 0,
    min: '',
    max: '',
    no_max: 0
  });
  const [filter, setFilter] = useState({
    limit: 50,
    filter: null,
    totalRenderedRecords: 0,
  });

  useEffect(() => {
    if (restaurantId) {
      handleFilterChange({ ...filter, restaurant_id: restaurantId })
    }
  }, [restaurantId])

  useEffect(() => {
    if (filter) {
      fetchChoices(filter);
    }
  }, [filter]);

  const fetchChoices = async (filter) => {
    try {
      if (restaurantId) {
        setIsLoading(true);

        const data = await getAllChoices(filter);
        if (data.choices.length !== 0 && data.totalCount !== choicesList.length) {
          setChoicesList([...choicesList, ...data.choices]);
          setTotalRecords(data.totalCount)
          setTotalRenderedRecords(choicesList.length)
        }

        setIsLoading(false);
      }
    } catch (error) {
      console.error('Error fetching choices', error);
    }
  };

  const handleCardClick = choice => {
    if(!canUpdate && !canDelete) return;
    
    setEditMode(true);
    setEditChoiceId(choice.id);
    setVisible(false);
  };

  const handleFilterChange = async (newFilter) => {
    setChoicesList([]);
    setFilter({ ...newFilter, totalRenderedRecords: 0 });
    setHasMore(true);
  };

  const handleLoadMore = () => {
    if (choicesList.length === totalRecords) {
      setHasMore(false);
    } else if (choicesList.length === filter.limit) {
      const updatedFilter = { ...filter, totalRenderedRecords: filter.limit };

      setFilter(updatedFilter);
      setTotalRenderedRecords(updatedFilter.totalRenderedRecords);
    } else {
      const updatedFilter = { ...filter, totalRenderedRecords: totalRenderedRecords + filter.limit };

      setFilter(updatedFilter);
      setTotalRenderedRecords(updatedFilter.totalRenderedRecords);
    }
  };

  const fetchChoiceProps = async (newChoiceId) => {
    try {
      if (editMode && editChoiceId) {
        const choice = await getSingleChoice(editChoiceId);
        const choiceIndex = choicesList.findIndex((c) => c.id === editChoiceId);

        if (choiceIndex !== -1) {
          if (!choice) {
            const updatedChoiceList = [...choicesList];
            updatedChoiceList.splice(choiceIndex, 1);

            setChoicesList(updatedChoiceList);
            setTotalRecords(totalRecords - 1);
            setTotalRenderedRecords(totalRenderedRecords - 1)
          } else {
            const updatedChoiceList = [...choicesList];
            updatedChoiceList[choiceIndex] = choice;

            setChoicesList(updatedChoiceList);
          }
        } else {
          setChoicesList([...choicesList, choice]);
        }
      } else if (visible) {
        const updatedChoiceList = [...choicesList];
        updatedChoiceList.unshift({ ...choiceDetails, id: newChoiceId });

        setChoicesList(updatedChoiceList);
        setTotalRecords(totalRecords + 1);
        setTotalRenderedRecords(totalRenderedRecords + 1)
      }
    } catch (error) {
      console.error('Error fetching choices.', error);
    }
  };

  const renderCardsInRows = () => {
    const rows = [];
    let currentRow = [];

    choicesList && choicesList.forEach((choice, index) => {
      currentRow.push(
        <div key={choice.id} className="d-flex">
          <Card
            title={
              <div className='product-name' title={choice.name}>
                {choice.name}
              </div>
            }
            header={
              choice.image ?
                <LazyLoadImage
                  src={choice.image}
                  alt={choice.name}
                  className='rounded-img'
                /> :
                <Icon
                  icon="ep:food"
                  className='card-icon'
                  height={100}
                  width={100}
                />}
            className="user-list-card"
            onClick={() => handleCardClick(choice)}
          />
        </div>
      );

      rows.push(
        <div key={index} className="d-flex">
          {currentRow}
        </div>
      );
      currentRow = [];
    });

    return rows;
  };

  return (
    <Layout2 items={ComboNav.items} navTitle={ComboNav.header}>
      <div className="d-flex flex-column w-100">
        <PageHeader title={'Choices'} />
        <div className="px-4 py-2">
          {(visible || (editMode && editChoiceId)) && (
            <AddChoice
              id={editChoiceId}
              choiceVisible={visible}
              editChoiceId={editChoiceId}
              editMode={editMode}
              choiceDetails={choiceDetails}
              setEditChoiceId={setEditChoiceId}
              setChoiceVisible={setVisible}
              setEditMode={setEditMode}
              fetchChoices={fetchChoiceProps}
              setChoiceDetails={setChoiceDetails}
              canCreate={canCreate}
              canUpdate={canUpdate}
              canDelete={canDelete}
            />
          )}
          {isLoading ? (
            <Loader />
          ) : (
            <>
              <Toast ref={toast} />
              <div className="d-flex flex-wrap">
                <InfiniteScroll
                  dataLength={choicesList.length}
                  next={handleLoadMore}
                  hasMore={hasMore}
                  className="card-container"
                >
                  {canCreate && 
                    <Card
                      title="Add Choice"
                      header={<Icon icon="tabler:plus" style={{ color: 'black' }} height={70} width={70} />}
                      className="add-user-group"
                      onClick={() => { setVisible(true) }}
                    />
                  }
                  {choicesList.length !== 0 && renderCardsInRows()}

                </InfiniteScroll>
              </div>
            </>
          )}
        </div>
      </div>
    </Layout2>
  );
}

export default ChoiceList;
